import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Bot, LogOut, MoreHorizontal, Pin, Plus, Settings } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDevBannerOverrides } from '@/lib/devBannerTriggers';
import { Urls } from '@/lib/navigation';
import { RAIL_WIDTH, PILL_GUTTER } from './railConstants';
import { Logomark } from './Logomark';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  PAPER_CHROME_RADIUS_CLASS,
  PAPER_CONTROL_RADIUS_CLASS,
  PAPER_ICON_CONTROL_ACTIVE_CLASS,
  PAPER_ICON_CONTROL_BEHAVIOR_CLASS,
  PAPER_ICON_CONTROL_CLASS,
  PAPER_ICON_CLASS,
  PAPER_ICON_COMPACT_CLASS,
  PAPER_ICON_SIZE,
  PAPER_ICON_STROKE_WIDTH,
  PAPER_ICON_TINY_CLASS,
} from './paperShapes';

export interface PinnedItem {
  name: string;
  on?: boolean;
  active?: boolean;
  icon?: ReactNode;
  /** Optional inline SVG markup (from the title generator). Rendered as
   *  the pill glyph in collapsed mode and as the leading icon in expanded
   *  mode; falls back to the first letter of `name` when missing. */
  iconSvg?: string | null;
  href?: string;
  /** Session id, when this item represents a chat. If set together with
   *  `SidebarRailProps.onItemSelect`, the rail calls that handler instead
   *  of plain navigation so the parent can pre-warm chat state. */
  sessionId?: string;
}

export type SidebarRailActive = 'chat' | 'agents' | 'settings';

export const useSidebarRailActive = (): SidebarRailActive | undefined => {
  const { pathname } = useLocation();
  if (pathname === '/' || pathname.startsWith('/agent/')) return 'chat';
  if (pathname.startsWith('/agents')) return 'agents';
  if (pathname.startsWith('/settings')) return 'settings';
  return undefined;
};

export interface RailAccountInfo {
  initials: string;
  displayName: string;
  metaLine: string;
  onSettings: () => void;
  onLogout?: () => void;
}

export interface SidebarRailProps {
  active?: SidebarRailActive;
  items?: SidebarRailActive[];
  pinned?: PinnedItem[];
  recents?: PinnedItem[];
  topbarHeightPx?: number;
  onNavigate?: (id: SidebarRailActive) => void;
  /** Called when the user clicks a pinned/recent item that carries a
   *  `sessionId`. Lets the parent pre-hydrate chat state before route
   *  navigation, matching the dashboard's smooth-switch behavior. */
  onItemSelect?: (sessionId: string) => void;
  bottomSlot?: ReactNode | ((expanded: boolean) => ReactNode);
  account?: RailAccountInfo;
}

type NavIcon = (props: { className?: string; strokeWidth?: number }) => ReactNode;

const NAV: Record<SidebarRailActive, { label: string; Icon: NavIcon }> = {
  chat: { label: 'New Agent', Icon: Plus },
  agents: { label: 'Agents', Icon: Bot },
  settings: { label: 'Settings', Icon: Settings },
};

const DEFAULT_ITEMS: SidebarRailActive[] = ['chat', 'agents'];

const EXPANDED_W = 248;
const COLLAPSED_W = RAIL_WIDTH;
const TRANSITION = 'width 260ms cubic-bezier(.22,.61,.36,1)';

interface PillGlyphProps {
  name: string;
  iconSvg?: string | null;
}

/** Collapsed-rail pill glyph: an inline SVG when the title generator
 *  supplied one, otherwise the legacy first-letter chip. */
const PillGlyph = (props: PillGlyphProps) => {
  if (props.iconSvg) {
    return (
      <span
        className={cn(
          PAPER_ICON_CLASS,
          'inline-flex items-center justify-center',
          '[&>svg]:w-full [&>svg]:h-full',
        )}
        aria-hidden
        dangerouslySetInnerHTML={{ __html: props.iconSvg }}
      />
    );
  }
  return (
    <span
      className={cn(
        'w-[18px] h-[18px] rounded-[4px] inline-flex items-center justify-center',
        'bg-[var(--bg-alt)] border border-solid border-[var(--hair)]',
        'text-[9px] font-semibold tracking-[0.04em] text-[var(--fg-3)]',
      )}
    >
      {(props.name || '?').trim().charAt(0).toUpperCase()}
    </span>
  );
};

interface ExpandedRailGlyphProps {
  iconSvg?: string | null;
}

/** Expanded-rail leading glyph: small inline SVG when present, otherwise
 *  the legacy bullet dot the rail rendered before. */
const ExpandedRailGlyph = (props: ExpandedRailGlyphProps) => {
  if (props.iconSvg) {
    return (
      <span
        className={cn(
          PAPER_ICON_TINY_CLASS,
          'inline-flex items-center justify-center',
          '[&>svg]:w-full [&>svg]:h-full',
        )}
        aria-hidden
        dangerouslySetInnerHTML={{ __html: props.iconSvg }}
      />
    );
  }
  return <span className="w-[4px] h-[4px] rounded-full bg-current" />;
};

export const SidebarRail = (props: SidebarRailProps) => {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const active = props.active;
  const pinned = props.pinned ?? [];
  const recents = props.recents ?? [];
  const topbarHeight = props.topbarHeightPx ?? 56;
  const handleNav = props.onNavigate;
  const items = props.items ?? DEFAULT_ITEMS;
  const width = expanded ? EXPANDED_W : COLLAPSED_W;
  const pill = useDevBannerOverrides().railPill;

  const onItemClick = (item: PinnedItem) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    const href = item.href;
    if (!href || href === '#') return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    if (item.sessionId && props.onItemSelect) {
      props.onItemSelect(item.sessionId);
      return;
    }
    navigate(href);
  };

  return (
    <nav
      aria-label="Main navigation"
      onMouseEnter={() => { if (!pill) setExpanded(true); }}
      onMouseLeave={() => { if (!pill) setExpanded(false); }}
      className={cn(
        'flex flex-col relative overflow-hidden',
        pill
          ? cn('my-[16px] bg-[var(--surface)] border border-solid border-[var(--hair)]', PAPER_CHROME_RADIUS_CLASS)
          : 'h-full bg-[var(--bg)] border-r border-solid border-[var(--hair)]',
      )}
      style={{
        width,
        transition: TRANSITION,
        zIndex: 3,
        flexShrink: 0,
        marginLeft: pill ? PILL_GUTTER : undefined,
        marginRight: pill ? PILL_GUTTER : undefined,
      }}
    >
      {/* brand row — height matches topbar so dividers align across columns.
          The whole row is a link to the Demos page so the logo doubles as a
          shortcut to "what can I build?". */}
      <Link
        to={Urls.demos()}
        aria-label="Browse demos"
        title="Browse demos"
        className={cn(
          'flex items-center gap-[10px] px-[18px] box-border shrink-0 text-[var(--fg)] no-underline',
          'transition-colors duration-150 hover:text-[var(--fg)]',
        )}
        style={{ height: topbarHeight }}
      >
        <Logomark size={PAPER_ICON_SIZE} />
        {expanded && (
          <span
            className="text-[16px] tracking-[-0.01em] whitespace-nowrap"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            Symbolica
          </span>
        )}
      </Link>

      <div className="border-t border-solid border-[var(--hair)]" />

      {/* primary nav */}
      <div
        className={cn(
          'flex flex-col gap-[2px] pt-[10px]',
          expanded ? 'px-[10px]' : 'px-[14px] items-center',
        )}
      >
        {items.map((id) => {
          const { label, Icon } = NAV[id];
          const isActive = id === active;
          return (
            <button
              key={id}
              type="button"
              onClick={() => handleNav?.(id)}
              title={expanded ? undefined : label}
              className={cn(
                'text-[12px] whitespace-nowrap',
                expanded
                  ? cn(
                      PAPER_CONTROL_RADIUS_CLASS,
                      PAPER_ICON_CONTROL_BEHAVIOR_CLASS,
                      'flex h-8 w-full items-center justify-start gap-[10px] px-[10px]',
                    )
                  : cn(PAPER_ICON_CONTROL_CLASS, 'flex items-center justify-center'),
                isActive && PAPER_ICON_CONTROL_ACTIVE_CLASS,
              )}
            >
              <Icon
                className={cn('shrink-0', PAPER_ICON_CLASS)}
                strokeWidth={PAPER_ICON_STROKE_WIDTH}
              />
              {expanded && <span>{label}</span>}
              {expanded && id === 'chat' && (
                <span className="ml-auto shrink-0 w-[6px] h-[6px] rounded-full bg-[var(--periwinkle)]" />
              )}
            </button>
          );
        })}
      </div>

      {/* divider between primary nav and pinned/recent sections */}
      {(pinned.length > 0 || (expanded && recents.length > 0)) && (
        <div className="h-px bg-[var(--hair)] mx-[14px] mt-[10px] mb-[10px]" />
      )}

      {/* pinned — collapsed stack.

          Each icon shows a styled tooltip with the agent / chat title on
          hover. We use a short open delay (60ms) so it feels immediate,
          and Radix portals the content to <body> so it renders above the
          floating rail without being clipped by `overflow-hidden`. */}
      {!expanded && pinned.length > 0 && (
        <>
          <div className="flex flex-col items-center gap-[4px] px-[10px]">
            {pinned.map((p, i) => (
              <Tooltip key={i} delayDuration={60}>
                <TooltipTrigger asChild>
                  <a
                    href={p.href ?? '#'}
                    onClick={onItemClick(p)}
                    aria-label={p.name}
                    className={cn(
                      PAPER_ICON_CONTROL_CLASS,
                      'relative',
                      p.active && 'bg-[var(--bg-alt)]',
                      p.on ? 'text-[var(--periwinkle)]' : 'text-[var(--fg-3)]',
                    )}
                  >
                    {p.icon ?? <PillGlyph name={p.name} iconSvg={p.iconSvg} />}
                    {p.on && (
                      <span className="absolute top-[4px] right-[4px] w-[5px] h-[5px] rounded-full bg-[var(--periwinkle)]" />
                    )}
                  </a>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={12}>
                  {p.name}
                </TooltipContent>
              </Tooltip>
            ))}
          </div>
        </>
      )}

      {/* pinned — expanded list */}
      {expanded && pinned.length > 0 && (
        <>
          <div className="px-[20px] pt-[10px] pb-[6px]">
            <div
              className={cn(
                'flex items-center gap-[10px]',
                'text-[11px] tracking-[0.14em] uppercase font-normal text-[var(--fg-3)]',
              )}
            >
              <span className="w-[16px] h-[16px] shrink-0 inline-flex items-center justify-center">
                <Pin className={PAPER_ICON_TINY_CLASS} strokeWidth={PAPER_ICON_STROKE_WIDTH} />
              </span>
              Pinned
            </div>
          </div>
          <div className="flex flex-col gap-px px-[10px] pb-[4px]">
            {pinned.map((p, i) => (
              <a
                key={i}
                href={p.href ?? '#'}
                onClick={onItemClick(p)}
                className={cn(
                  'flex items-center gap-[10px] h-[30px] px-[10px]',
                  PAPER_CONTROL_RADIUS_CLASS,
                  'text-[12px] whitespace-nowrap overflow-hidden text-ellipsis',
                  'transition-colors duration-150',
                  p.active
                    ? 'text-[var(--fg)] bg-[var(--bg-alt)]'
                    : 'text-[var(--fg-3)] hover:text-[var(--fg)] hover:bg-[var(--bg-alt)]',
                )}
              >
                <span
                  className={cn(
                    'w-[16px] h-[16px] shrink-0 inline-flex items-center justify-center',
                    p.on ? 'text-[var(--periwinkle)]' : 'text-[var(--fg-4)]',
                  )}
                >
                  {p.icon ?? <ExpandedRailGlyph iconSvg={p.iconSvg} />}
                </span>
                <span className="overflow-hidden text-ellipsis">{p.name}</span>
              </a>
            ))}
          </div>
        </>
      )}

      {/* recents — expanded only */}
      {expanded && recents.length > 0 && (
        <>
          <div className="px-[20px] pt-[14px] pb-[6px]">
            <div
              className={cn(
                'flex items-center gap-[10px]',
                'text-[11px] tracking-[0.14em] uppercase font-normal text-[var(--fg-3)]',
              )}
            >
              <span className="w-[16px] h-[16px] shrink-0" />
              Recent
            </div>
          </div>
          <div className="flex flex-col gap-px px-[10px] pb-[8px]">
            {recents.map((r, i) => (
              <a
                key={i}
                href={r.href ?? '#'}
                onClick={onItemClick(r)}
                className={cn(
                  'flex items-center gap-[10px] h-[30px] px-[10px]',
                  PAPER_CONTROL_RADIUS_CLASS,
                  'text-[12px] whitespace-nowrap overflow-hidden text-ellipsis',
                  'transition-colors duration-150',
                  r.active
                    ? 'text-[var(--fg)] bg-[var(--bg-alt)]'
                    : 'text-[var(--fg-3)] hover:text-[var(--fg)] hover:bg-[var(--bg-alt)]',
                )}
              >
                <span
                  className={cn(
                    'w-[16px] h-[16px] shrink-0 inline-flex items-center justify-center',
                    r.on ? 'text-[var(--periwinkle)]' : 'text-[var(--fg-4)]',
                  )}
                >
                  <ExpandedRailGlyph iconSvg={r.iconSvg} />
                </span>
                <span className="overflow-hidden text-ellipsis">{r.name}</span>
              </a>
            ))}
          </div>
        </>
      )}

      <div className="mt-auto shrink-0 flex flex-col">
        {props.bottomSlot && (
          <div
            className={cn(
              'shrink-0',
              expanded ? 'px-[10px] pb-[10px]' : 'px-[14px] pb-[10px] flex justify-center',
            )}
          >
            {typeof props.bottomSlot === 'function' ? props.bottomSlot(expanded) : props.bottomSlot}
          </div>
        )}
        {props.account && (
          <RailAccountFooter account={props.account} expanded={expanded} active={active === 'settings'} />
        )}
      </div>
    </nav>
  );
};

interface RailAccountFooterProps {
  account: RailAccountInfo;
  expanded: boolean;
  active: boolean;
}

const RailAccountFooter = ({ account, expanded, active }: RailAccountFooterProps) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{ left: number; bottom: number } | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // Anchor menu's left edge just inside the trigger's left edge so it
    // always opens rightward into the chat column. Avoids spillover off
    // the viewport when the floating rail is narrow.
    setMenuPos({
      left: rect.left + 8,
      bottom: window.innerHeight - rect.top + 8,
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const rowCls =
    `flex items-center gap-[8px] w-full px-[8px] py-[6px] ${PAPER_CONTROL_RADIUS_CLASS} text-[12px] ` +
    'text-[var(--fg-2)] hover:bg-[var(--bg-alt)] hover:text-[var(--fg)] ' +
    'cursor-pointer text-left transition-colors duration-150';

  return (
    <div className="shrink-0 border-t border-solid border-[var(--hair)]">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={expanded ? undefined : account.displayName}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="paper-account-menu"
        className={cn(
          'w-full flex items-center gap-[10px] text-left transition-colors duration-150',
          expanded ? 'px-[14px] py-[10px]' : 'px-[14px] py-[10px] justify-center',
          active || open ? 'bg-[var(--periwinkle-wash)]' : 'hover:bg-[var(--bg-alt)]',
        )}
      >
        <span
          className={cn(
            'shrink-0 inline-flex items-center justify-center rounded-full',
            'w-[28px] h-[28px]',
            'bg-[var(--periwinkle-wash)] text-[var(--periwinkle)]',
            'border border-solid border-[var(--periwinkle-veil)]',
            'text-[10px] font-semibold tracking-[0.06em]',
          )}
        >
          {account.initials}
        </span>
        {expanded && (
          <>
            <span className="min-w-0 flex-1 flex flex-col">
              <span className="text-[12px] font-semibold text-[var(--fg)] truncate">
                {account.displayName}
              </span>
              <span className="text-[11px] text-[var(--fg-3)] truncate">
                {account.metaLine}
              </span>
            </span>
            <MoreHorizontal
              className={cn('shrink-0 text-[var(--fg-3)]', PAPER_ICON_CLASS)}
              strokeWidth={PAPER_ICON_STROKE_WIDTH}
            />
          </>
        )}
      </button>
      {open && menuPos && createPortal(
        <div
          ref={menuRef}
          id="paper-account-menu"
          role="menu"
          style={{ position: 'fixed', left: menuPos.left, bottom: menuPos.bottom, zIndex: 80 }}
          className={cn(
            'flex flex-col',
            'min-w-[180px] p-[4px] rounded-lg shadow-lg',
            'bg-[var(--surface)] border border-solid border-[var(--hair)]',
          )}
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              account.onSettings();
            }}
            className={rowCls}
          >
            <Settings
              className={cn('shrink-0', PAPER_ICON_COMPACT_CLASS)}
              strokeWidth={PAPER_ICON_STROKE_WIDTH}
            />
            Settings
          </button>
          {account.onLogout && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                account.onLogout?.();
              }}
              className={rowCls}
            >
              <LogOut
                className={cn('shrink-0', PAPER_ICON_COMPACT_CLASS)}
                strokeWidth={PAPER_ICON_STROKE_WIDTH}
              />
              Log out
            </button>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
};
